import React, { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  MessageSquare, Plus, Search, Send, Paperclip, Loader2, Check, CheckCheck,
  Users, Plane, X, FileText, ChevronsUpDown, CircleDot,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";

const SCOPE_LABEL = { mine: "Mis chats", all: "Chat del equipo", solved: "Resueltos" };

function useIsMobile() {
  const query = "(max-width: 767px)";
  const [mobile, setMobile] = useState(() => (typeof window !== "undefined" ? window.matchMedia(query).matches : false));
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e) => setMobile(e.matches);
    mq.addEventListener("change", handler);
    setMobile(mq.matches);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return mobile;
}

export const fmtChatTime = (iso) => {
  try {
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};
const initials = (name) => (name || "?").split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();

// ============================= Selector de personas =============================
function PeopleSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const { data } = useQuery({ queryKey: ["chat-users"], queryFn: async () => (await api.get("/chat/users")).data });
  const users = data?.items || [];
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  const names = users.filter((u) => value.includes(u.id)).map((u) => u.name);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="chat-people-trigger">
          <span className={`truncate ${names.length ? "" : "text-muted-foreground"}`}>{names.length ? names.join(", ") : "Elige destinatarios…"}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar agente…" />
          <CommandList>
            <CommandEmpty>Sin agentes.</CommandEmpty>
            <CommandGroup>
              {users.map((u) => (
                <CommandItem key={u.id} value={u.name} onSelect={() => toggle(u.id)} data-testid={`chat-people-opt-${u.id}`}>
                  <Check className={`mr-2 h-4 w-4 ${value.includes(u.id) ? "opacity-100" : "opacity-0"}`} />
                  {u.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ============================= Buscador de reserva =============================
function ReservaSearch({ value, label, onSelect }) {
  const [q, setQ] = useState("");
  const { data } = useQuery({
    queryKey: ["chat-reserva-search", q],
    queryFn: async () => (await api.get("/chat/reservas/search", { params: { q } })).data,
    enabled: q.trim().length >= 2,
  });
  const items = data?.items || [];
  if (value) {
    return (
      <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2 text-sm" data-testid="chat-reserva-selected">
        <span className="flex items-center gap-1.5 truncate"><Plane className="h-3.5 w-3.5 text-primary" />{label}</span>
        <button type="button" onClick={() => onSelect(null, null)} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
      </div>
    );
  }
  return (
    <div className="relative">
      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar viaje/reserva (opcional)…" className="pl-8" data-testid="chat-reserva-search" />
      {items.length > 0 && (
        <div className="absolute z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-md border bg-popover shadow-md">
          {items.map((it) => (
            <button key={it.reserva_id} type="button" onClick={() => { onSelect(it.reserva_id, it.label); setQ(""); }}
              className="block w-full truncate px-3 py-2 text-left text-sm hover:bg-muted" data-testid={`chat-reserva-opt-${it.reserva_id}`}>
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================= Nueva conversación =============================
export function NewConversationDialog({ open, onClose, onCreated, presetReserva }) {
  const [subject, setSubject] = useState("");
  const [people, setPeople] = useState([]);
  const [text, setText] = useState("");
  const [reserva, setReserva] = useState({ id: null, label: null });
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setSubject(""); setPeople([]); setText(""); setFile(null);
      setReserva(presetReserva ? { id: presetReserva.id, label: presetReserva.label } : { id: null, label: null });
    }
  }, [open, presetReserva]);

  const mut = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("subject", subject);
      fd.append("text", text);
      fd.append("participants", JSON.stringify(people));
      if (reserva.id) fd.append("reserva_id", reserva.id);
      if (file) fd.append("file", file);
      return (await api.post("/chat/conversations", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (d) => { toast.success("Conversación creada"); onCreated?.(d); onClose(); },
    onError: (e) => toast.error(apiErrorMessage(e, "No se pudo crear")),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="chat-new-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MessageSquare className="h-4 w-4 text-primary" />Nueva conversación</DialogTitle>
          <DialogDescription className="text-xs">Escribe a uno o varios compañeros. Puedes vincularla a un viaje/reserva y adjuntar un archivo.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Asunto *</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Ej. Contabilidad, Dudas reserva…" data-testid="chat-new-subject" /></div>
          <div><Label className="text-xs">Para *</Label><PeopleSelect value={people} onChange={setPeople} /></div>
          {!presetReserva && <div><Label className="text-xs">Viaje / Reserva (opcional)</Label><ReservaSearch value={reserva.id} label={reserva.label} onSelect={(id, label) => setReserva({ id, label })} /></div>}
          {presetReserva && <div className="flex items-center gap-1.5 rounded-md border bg-muted/40 px-3 py-2 text-sm"><Plane className="h-3.5 w-3.5 text-primary" />{presetReserva.label}</div>}
          <div><Label className="text-xs">Mensaje</Label><Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Escribe tu mensaje…" data-testid="chat-new-text" /></div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="chat-new-file-input" />
            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}><Paperclip className="mr-1.5 h-4 w-4" />{file ? "Cambiar adjunto" : "Adjuntar"}</Button>
            {file && <span className="truncate text-xs text-muted-foreground">{file.name}</span>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => mut.mutate()} disabled={!subject || people.length === 0 || mut.isPending} data-testid="chat-new-submit">
            {mut.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}Crear
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================= Hilo de conversación =============================
export function ConversationThread({ conversationId }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);

  const { data, isLoading } = useQuery({
    queryKey: ["chat-conv", conversationId],
    queryFn: async () => (await api.get(`/chat/conversations/${conversationId}`)).data,
    refetchInterval: 4000,
    enabled: !!conversationId,
  });
  const conv = data?.conversation;
  const messages = data?.messages || [];

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const sendMut = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("text", text);
      if (file) fd.append("file", file);
      return (await api.post(`/chat/conversations/${conversationId}/messages`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: () => { setText(""); setFile(null); qc.invalidateQueries({ queryKey: ["chat-conv", conversationId] }); qc.invalidateQueries({ queryKey: ["chat-list"] }); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const solveMut = useMutation({
    mutationFn: async (solved) => (await api.patch(`/chat/conversations/${conversationId}`, { solved })).data,
    onSuccess: (d) => { toast.success(d.solved ? "Marcada como resuelta" : "Reabierta"); qc.invalidateQueries({ queryKey: ["chat-conv", conversationId] }); qc.invalidateQueries({ queryKey: ["chat-list"] }); },
  });

  const viewAttachment = async (m) => {
    try {
      const res = await api.get(`/chat/conversations/${conversationId}/messages/${m.id}/attachment`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  if (isLoading) return <Skeleton className="h-full min-h-[420px] w-full" />;
  if (!conv) return null;

  return (
    <div className="flex h-full flex-col" data-testid="chat-thread">
      {/* Cabecera */}
      <div className="flex items-start justify-between gap-3 border-b p-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="truncate font-heading text-base font-semibold">{conv.subject}</h3>
            {conv.solved && <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-[10px] text-emerald-700">Resuelto</Badge>}
          </div>
          <p className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
            <Users className="h-3 w-3" />{(conv.participants || []).map((p) => p.name).join(", ")}
          </p>
          {conv.reserva_label && <p className="mt-0.5 flex items-center gap-1 text-xs text-primary"><Plane className="h-3 w-3" />{conv.reserva_label}</p>}
        </div>
        <Button size="sm" variant={conv.solved ? "outline" : "secondary"} onClick={() => solveMut.mutate(!conv.solved)} disabled={solveMut.isPending} data-testid="chat-solve-toggle">
          <CheckCheck className="mr-1.5 h-4 w-4" />{conv.solved ? "Reabrir" : "Resolver"}
        </Button>
      </div>

      {/* Mensajes */}
      <div className="flex-1 space-y-3 overflow-y-auto bg-muted/20 p-4" data-testid="chat-messages">
        {messages.map((m) => {
          const own = m.author_id === user?.id;
          return (
            <div key={m.id} className={`flex gap-2 ${own ? "flex-row-reverse" : ""}`}>
              <UserAvatar name={m.author_name} color={m.author_color || "slate"} size="md" />
              <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 ${own ? "rounded-tr-sm bg-primary text-primary-foreground" : "rounded-tl-sm bg-card border"}`}>
                {!own && <p className="mb-0.5 text-[11px] font-semibold opacity-80">{m.author_name}</p>}
                {m.text && <p className="whitespace-pre-wrap break-words text-sm">{m.text}</p>}
                {m.attachment && (
                  <button onClick={() => viewAttachment(m)} className={`mt-1.5 flex items-center gap-1.5 rounded-md px-2 py-1 text-xs ${own ? "bg-primary-foreground/15 hover:bg-primary-foreground/25" : "bg-muted hover:bg-muted/70"}`} data-testid={`chat-attach-${m.id}`}>
                    <FileText className="h-3.5 w-3.5" /><span className="truncate">{m.attachment.filename}</span>
                  </button>
                )}
                <p className={`mt-1 text-right text-[10px] ${own ? "opacity-70" : "text-muted-foreground"}`}>{fmtChatTime(m.created_at)}</p>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      <div className="border-t p-3">
        {file && <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground"><Paperclip className="h-3.5 w-3.5" /><span className="truncate">{file.name}</span><button onClick={() => setFile(null)}><X className="h-3.5 w-3.5" /></button></div>}
        <div className="flex items-end gap-2">
          <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="chat-msg-file-input" />
          <Button type="button" size="icon" variant="ghost" className="h-9 w-9 shrink-0" onClick={() => fileRef.current?.click()} title="Adjuntar"><Paperclip className="h-4 w-4" /></Button>
          <Textarea rows={1} value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (text.trim() || file) sendMut.mutate(); } }}
            placeholder="Escribe un mensaje…  (Enter para enviar)" className="min-h-[40px] resize-none" data-testid="chat-msg-input" />
          <Button size="icon" className="h-9 w-9 shrink-0" onClick={() => sendMut.mutate()} disabled={(!text.trim() && !file) || sendMut.isPending} data-testid="chat-msg-send">
            {sendMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================= Lista de conversaciones =============================
function ConversationList({ scope, q, selectedId, onSelect }) {
  const { data, isLoading } = useQuery({
    queryKey: ["chat-list", scope, q],
    queryFn: async () => (await api.get("/chat/conversations", { params: { scope, q: q || undefined } })).data,
    refetchInterval: 5000,
  });
  const items = data?.items || [];
  if (isLoading) return <div className="space-y-2 p-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>;
  if (items.length === 0) return <p className="p-6 text-center text-sm text-muted-foreground" data-testid="chat-list-empty">No hay conversaciones.</p>;
  return (
    <div className="divide-y" data-testid="chat-list">
      {items.map((c) => (
        <button key={c.id} onClick={() => onSelect(c.id)}
          className={`flex w-full flex-col gap-1 px-3.5 py-3 text-left transition-colors ${selectedId === c.id ? "bg-accent" : "hover:bg-muted/50"}`}
          data-testid={`chat-item-${c.id}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5 truncate font-medium text-sm">
              {c.unread && <CircleDot className="h-3 w-3 shrink-0 text-primary" data-testid={`chat-unread-${c.id}`} />}
              {c.subject}
            </span>
            <span className="shrink-0 text-[10px] text-muted-foreground">{fmtChatTime(c.last_message_at)}</span>
          </div>
          <span className="truncate text-[11px] text-muted-foreground">{(c.participants || []).map((p) => p.name).join(", ")}</span>
          {c.last_message_preview && <span className={`truncate text-xs ${c.unread ? "font-medium text-foreground" : "text-muted-foreground"}`}>{c.last_message_by ? `${c.last_message_by.split(" ")[0]}: ` : ""}{c.last_message_preview}</span>}
          <div className="flex items-center gap-1.5">
            {c.reserva_label && <Badge variant="outline" className="max-w-full truncate text-[9px]"><Plane className="mr-1 h-2.5 w-2.5" />{c.reserva_label}</Badge>}
            {c.solved && <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-[9px] text-emerald-700">Resuelto</Badge>}
          </div>
        </button>
      ))}
    </div>
  );
}

// ============================= Página principal =============================
export default function ChatPage() {
  const [params] = useSearchParams();
  const [scope, setScope] = useState(params.get("view") || "mine");
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const isMobile = useIsMobile();

  return (
    <div className="space-y-4" data-testid="chat-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight"><MessageSquare className="h-6 w-6 text-primary" />Chat Interno</h1>
          <p className="text-sm text-muted-foreground">{SCOPE_LABEL[scope]} · conversaciones internas del equipo.</p>
        </div>
        <Button onClick={() => setNewOpen(true)} data-testid="chat-new-btn"><Plus className="mr-1.5 h-4 w-4" />Nueva conversación</Button>
      </div>

      <Card className="overflow-hidden">
        <div className="grid h-[calc(100vh-15rem)] min-h-[480px] grid-cols-1 md:grid-cols-[340px_1fr]">
          {/* Lista */}
          <div className="flex flex-col border-r">
            <div className="border-b p-2">
              <Tabs value={scope} onValueChange={(v) => { setScope(v); setSelectedId(null); }}>
                <TabsList className="grid h-8 w-full grid-cols-3" data-testid="chat-tabs">
                  <TabsTrigger value="mine" className="text-xs" data-testid="chat-tab-mine">Mis chats</TabsTrigger>
                  <TabsTrigger value="all" className="text-xs" data-testid="chat-tab-all">Equipo</TabsTrigger>
                  <TabsTrigger value="solved" className="text-xs" data-testid="chat-tab-solved">Resueltos</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <div className="border-b p-2.5">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por asunto…" className="h-9 pl-8" data-testid="chat-search" />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              <ConversationList scope={scope} q={q} selectedId={selectedId} onSelect={setSelectedId} />
            </div>
          </div>
          {/* Hilo */}
          <div className="hidden md:block">
            {selectedId ? <ConversationThread conversationId={selectedId} /> : (
              <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground" data-testid="chat-empty">
                <MessageSquare className="h-10 w-10 opacity-30" />
                <p className="text-sm">Elige una conversación o crea una nueva.</p>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* En móvil, hilo en diálogo */}
      {selectedId && isMobile && (
        <Dialog open onOpenChange={(o) => !o && setSelectedId(null)}>
          <DialogContent className="block h-[85vh] max-w-2xl p-0">
            <DialogHeader className="sr-only"><DialogTitle>Conversación</DialogTitle></DialogHeader>
            <ConversationThread conversationId={selectedId} />
          </DialogContent>
        </Dialog>
      )}

      <NewConversationDialog open={newOpen} onClose={() => setNewOpen(false)} onCreated={(c) => setSelectedId(c.id)} />
    </div>
  );
}
